/**
 * Row-id helpers for {@link DataGridTable}.
 *
 * MUI X DataGrid requires a stable, unique id per row. mui-datatables had no
 * such requirement (it keyed rows positionally by array index), so several API
 * payloads in this app carry no `id` field. These helpers reproduce that
 * index-based keying as a fallback while still honoring a real `id` when present
 * or a caller-provided resolver.
 *
 * Kept free of MUI imports so the logic is unit-testable without rendering the
 * grid (which is impractical under the project's vitest + MUI 7 interop setup).
 */

/**
 * Tipos locales a proposito: este modulo se mantiene libre de imports de MUI para poder
 * testear la logica sin renderizar la grilla, y un import de tipos igual ataria el archivo
 * al paquete.
 */
export type RowId = string | number;

export type GridRowLike = Record<string, unknown>;

export type RowIdResolver = (row: GridRowLike) => RowId;

/**
 * Lo que se acepta de quien llama. Es mas laxo que RowIdResolver a proposito: un resolver
 * escrito a mano devuelve el campo crudo de la fila, que es `unknown` sobre un Record
 * generico. Se confia en que produce un id usable, igual que antes de tipar.
 */
export type CallerRowIdResolver = (row: GridRowLike) => unknown;

export const ROW_INDEX_KEY = "__rowIndex";

/**
 * Builds the `getRowId` function DataGrid will use.
 * Honors a caller-provided resolver; otherwise falls back to `row.id` and then
 * to the injected array-index key.
 *
 * @param getRowId Caller-provided row id resolver.
 */
export const resolveRowId = (getRowId?: CallerRowIdResolver): RowIdResolver =>
  (getRowId as RowIdResolver | undefined) ??
  ((row) => (row.id ?? row[ROW_INDEX_KEY]) as RowId);

/**
 * Normalizes rows so every one has a usable id.
 * When no custom resolver is given and a row lacks `id`, an array-index key is
 * injected (rows that already have `id` are left untouched). With a custom
 * resolver, rows are returned as-is.
 *
 * @param rows      Source rows.
 * @param getRowId  Caller-provided row id resolver.
 */
export const normalizeRows = (rows?: GridRowLike[], getRowId?: CallerRowIdResolver): GridRowLike[] => {
  const safeRows = rows ?? [];
  if (getRowId) {
    return safeRows;
  }
  return safeRows.map((row, index) =>
    row.id !== undefined ? row : { ...row, [ROW_INDEX_KEY]: index }
  );
};
